import { useEffect, useMemo, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { apiPost } from '../lib/api';
import {
  Btn, Card, Select, Table, SectionHeader,
  Empty, Spinner, Toast, Stat, Modal, Input, Badge,
} from './ui';

type Contact = Schema['Contact']['type'];
type Client  = Schema['Client']['type'];

const PAGE_SIZE = 50;

export default function ContactList() {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [clients,       setClients]       = useState<Client[]>([]);
  const [contacts,      setContacts]      = useState<Contact[]>([]);
  const [filtered,      setFiltered]      = useState<Contact[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [clientFilter,  setClientFilter]  = useState('');
  const [sourceFilter,  setSourceFilter]  = useState('');
  const [listFilter,    setListFilter]    = useState('');
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(0);
  const [suppressing,   setSuppressing]   = useState<string | null>(null);
  const [suppressModal, setSuppressModal] = useState<Contact | null>(null);
  const [suppressReason,setSuppressReason]= useState('');
  const [toast,         setToast]         = useState<{ msg: string; type: 'success'|'error' } | null>(null);

  // CSV import state
  const csvInputRef                         = useRef<HTMLInputElement>(null);
  const [csvModal,      setCsvModal]        = useState(false);
  const [csvRows,       setCsvRows]         = useState<Record<string, string>[]>([]);
  const [csvClient,     setCsvClient]       = useState('');
  const [csvImporting,  setCsvImporting]    = useState(false);
  const [csvProgress,   setCsvProgress]     = useState('');
  const [csvErrors,     setCsvErrors]       = useState<string[]>([]);
  const [csvListName,   setCsvListName]    = useState('');

  // Push to Ad Platform state (shown after CSV import)
  const [pushToMeta,    setPushToMeta]   = useState<{ clientId: string; listName: string; count: number; personalEmailCount: number } | null>(null);
  const [pushingMeta,   setPushingMeta]  = useState(false);
  const [pushPlatforms, setPushPlatforms] = useState<Set<string>>(new Set());
  const [pushResults,   setPushResults]  = useState<Record<string, string>>({}); // platform -> status msg
  const [enriching,    setEnriching]    = useState(false);
  const [enrichResult, setEnrichResult] = useState<string>('');
  const enrichPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Bulk select + delete
  const [selected,     setSelected]     = useState<Set<string>>(new Set());
  const [deleteModal,  setDeleteModal]  = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const [deleteProgress, setDeleteProgress] = useState('');

  // Create audience from selected
  const [audModal,       setAudModal]       = useState(false);
  const [audName,        setAudName]        = useState('');
  const [audCreating,    setAudCreating]    = useState(false);
  const [audProgress,    setAudProgress]    = useState('');

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let result = contacts;
    if (clientFilter) result = result.filter(c => c.clientId === clientFilter);
    if (sourceFilter) result = result.filter((c: any) => c.source === sourceFilter);
    if (listFilter)   result = result.filter((c: any) => c.listName === listFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.email?.toLowerCase().includes(q)       ||
        c.firstName?.toLowerCase().includes(q)   ||
        c.lastName?.toLowerCase().includes(q)    ||
        c.companyName?.toLowerCase().includes(q) ||
        c.jobTitle?.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
    setPage(0);
  }, [contacts, clientFilter, sourceFilter, listFilter, search]);

  async function listAllContacts() {
    const all: Contact[] = [];
    let next: string | null | undefined = undefined;
    do {
      const res: any = await db.models.Contact.list({ limit: 1000, nextToken: next });
      all.push(...(res.data ?? []));
      next = res.nextToken;
    } while (next);
    return all;
  }

  async function load() {
    setLoading(true);
    try {
      const [cl, allContacts] = await Promise.all([
        db.models.Client.list(),
        listAllContacts(),
      ]);
      setClients(cl.data ?? []);
      const sorted = [...allContacts].filter(Boolean).sort((a: any, b: any) =>
        new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime()
      );
      setContacts(sorted);
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg: string, type: 'success'|'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  // ─── CSV helpers ────────────────────────────────────────────────────────────

  function parseCsvText(text: string): Record<string, string>[] {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim().split('\n');
    if (lines.length < 2) return [];
    // Simple CSV parse (handles quoted fields)
    function splitLine(line: string): string[] {
      const result: string[] = [];
      let cur = '', inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuote = !inQuote; continue; }
        if (ch === ',' && !inQuote) { result.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      result.push(cur.trim());
      return result;
    }
    const headers = splitLine(lines[0]).map(h => h.toLowerCase().replace(/[\s_-]/g, ''));
    const FIELD_MAP = {
      email:         ['email', 'emailaddress', 'e-mail'],
      personalEmail: ['personalemail', 'prospectemail', 'prospecoemail', 'prospeopersonemail', 'personalemail2', 'secondaryemail', 'tertiaryemail'],
      firstName:     ['firstname', 'first', 'prospecofirstname', 'prospecopersonfirstname'],
      lastName:      ['lastname', 'last', 'prospecolastname', 'prospecopersonlastname'],
      phone:         ['phone', 'phonenumber', 'mobile', 'cell', 'mobilephone', 'mobilephonenumber', 'prospecomobile'],
      phone2:        ['homephone', 'workdirectphone', 'directphone', 'corporatephone', 'otherphone', 'alternatephone'],
      jobTitle:      ['jobtitle', 'title', 'position', 'role', 'prospecojobtitle'],
      companyName:   ['companyname', 'company', 'organization', 'org', 'prospecocompanyname'],
      companyDomain: ['companydomain', 'domain', 'website'],
      linkedinUrl:   ['linkedinurl', 'linkedin', 'personlinkedinurl', 'linkedinprofile', 'linkedinprofileurl', 'prospecopersonlinkedinurl'],
      seniority:     ['seniority', 'prospecojobseniority'],
      department:    ['department', 'departments', 'dept', 'prospecojobdepartment'],
      city:          ['city', 'prospecopersoncity'],
      state:         ['state', 'province', 'region', 'prospecopersonstate'],
      zip:           ['zip', 'zipcode', 'postalcode', 'postal', 'postcode', 'prospecopersonpostalcode'],
      country:       ['country', 'prospecopersoncountry'],
      street:        ['street', 'address', 'address1', 'streetaddress', 'street1'],
    } as const;
    const colMap: Record<string, keyof typeof FIELD_MAP> = {};
    headers.forEach((h, i) => {
      for (const [field, aliases] of Object.entries(FIELD_MAP)) {
        if ((aliases as readonly string[]).includes(h)) { colMap[i] = field as keyof typeof FIELD_MAP; break; }
      }
    });
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = splitLine(line);
      const row: Record<string, string> = {};
      // Use first non-empty value when multiple columns map to the same field
      vals.forEach((v, i) => { if (colMap[i] && !row[colMap[i]]) row[colMap[i]] = v; });
      return row;
    });
  }

  function handleCsvFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      const text = (evt.target?.result as string) ?? '';
      const rows = parseCsvText(text);
      if (rows.length === 0) { showToast('CSV appears empty or unreadable', 'error'); return; }
      setCsvRows(rows);
      setCsvClient(clients.length === 1 ? clients[0].id : '');
      setCsvListName('');
      setCsvErrors([]);
      setCsvProgress('');
      setCsvModal(true);
    };
    reader.readAsText(file);
    // Reset so same file can be re-selected
    e.target.value = '';
  }

  async function importCsv() {
    if (!csvClient) { setCsvErrors(['Please select a client.']); return; }
    if (!csvListName.trim()) { setCsvErrors(['Please enter a list name (e.g. "Product A shoppers").']); return; }
    setCsvImporting(true);
    setCsvErrors([]);
    let ok = 0; let personalEmailCount = 0; const errs: string[] = [];
    for (let i = 0; i < csvRows.length; i++) {
      const row = csvRows[i];
      setCsvProgress(`Importing ${i + 1} of ${csvRows.length}…`);
      try {
        await db.models.Contact.create({
          clientId:      csvClient,
          source:        'csv',
          listName:      csvListName.trim() || undefined,
          email:         row.email         || undefined,
          firstName:     row.firstName     || undefined,
          lastName:      row.lastName      || undefined,
          phone:         row.phone || (row as any).phone2 || undefined,
          jobTitle:      row.jobTitle      || undefined,
          companyName:   row.companyName   || undefined,
          companyDomain: row.companyDomain || undefined,
          linkedinUrl:   row.linkedinUrl   || undefined,
          personalEmail: (row as any).personalEmail || undefined,
          seniority:     row.seniority     || undefined,
          department:    row.department    || undefined,
          city:          row.city          || undefined,
          state:         row.state         || undefined,
          zip:           (row as any).zip   || undefined,
          country:       row.country       || undefined,
          street:        (row as any).street || undefined,
        } as any);
        if ((row as any).personalEmail) personalEmailCount++;
        ok++;
      } catch (e: any) {
        errs.push(`Row ${i + 1}${row.email ? ` (${row.email})` : ''}: ${e.message}`);
      }
    }
    setCsvImporting(false);
    setCsvProgress('');
    if (errs.length) {
      setCsvErrors(errs.slice(0, 10));
      showToast(`Imported ${ok} contacts (${errs.length} errors)`, 'error');
    } else {
      setCsvModal(false);
      await load();
      // Offer to push to ad platforms immediately
      setPushPlatforms(new Set());
      setPushResults({});
      setPushToMeta({ clientId: csvClient, listName: csvListName.trim(), count: ok, personalEmailCount });
    }
  }

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    const pageIds = paginated.map(c => c.id);
    const allSelected = pageIds.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) { pageIds.forEach(id => next.delete(id)); }
      else             { pageIds.forEach(id => next.add(id)); }
      return next;
    });
  }

  async function handleDeleteSelected() {
    setDeleting(true);
    setDeleteModal(false);
    const toDelete = [...selected];
    const deletedIds = new Set<string>();
    const errs: string[] = [];

    // Run in batches of 10 concurrent to avoid sequential bottleneck
    const BATCH = 10;
    for (let i = 0; i < toDelete.length; i += BATCH) {
      const batch = toDelete.slice(i, i + BATCH);
      setDeleteProgress(`Deleting ${Math.min(i + BATCH, toDelete.length)} of ${toDelete.length}…`);
      const results = await Promise.allSettled(
        batch.map(id => db.models.Contact.delete({ id }))
      );
      results.forEach((r, j) => {
        if (r.status === 'fulfilled') {
          const res = r.value as any;
          if (res.errors?.length) errs.push(res.errors[0].message);
          else deletedIds.add(batch[j]);
        } else {
          errs.push(r.reason?.message ?? 'Unknown error');
        }
      });
    }

    setDeleting(false);
    setDeleteProgress('');
    setSelected(new Set());
    setContacts(prev => prev.filter(c => !deletedIds.has(c.id)));
    if (errs.length) showToast(`Deleted ${deletedIds.size}, failed ${errs.length}: ${errs[0]}`, 'error');
    else showToast(`Deleted ${deletedIds.size} contact${deletedIds.size !== 1 ? 's' : ''}`);
  }

  async function handlePushToMeta() {
    if (!pushToMeta) return;
    setPushingMeta(true);
    setPushResults({});
    try {
      // Export to S3 + trigger Meta prospeo sync
      const res = await apiPost<{ exported: number; syncId: string; audienceId: string; s3ExportKey: string }>(
        '/contacts/export',
        { clientId: pushToMeta.clientId, listName: pushToMeta.listName || undefined },
      );
      const results: Record<string, string> = {};
      const syncIdsToSave: Record<string, string> = {};

      // Create per-audience Hightouch syncs for all selected platforms (including Meta)
      for (const platform of [...pushPlatforms]) {
        try {
          const sr = await apiPost<{ syncId: string; alreadyDone?: boolean }>('/hightouch/setup', {
            clientId:      pushToMeta.clientId,
            type:          platform,
            audienceId:    res.audienceId,
            s3KeyOverride: res.s3ExportKey,
          });
          // Trigger the sync so data flows immediately
          await apiPost('/sync/trigger', { syncId: sr.syncId, fullResync: true });
          results[platform] = `✓ sync #${sr.syncId}`;
          syncIdsToSave[`hightouchSyncId${platform.charAt(0).toUpperCase()}${platform.slice(1)}`] = String(sr.syncId);
        } catch (e: any) {
          results[platform] = `✗ ${e.message}`;
        }
      }

      // Save sync IDs so platform chips light up green in the Audiences tab
      if (res.audienceId && Object.keys(syncIdsToSave).length > 0) {
        try { await (db.models as any).Audience.update({ id: res.audienceId, ...syncIdsToSave }); } catch { /* non-critical */ }
      }

      setPushResults(results);
      showToast(`Pushed ${res.exported} contacts to ${pushPlatforms.size} platform${pushPlatforms.size !== 1 ? 's' : ''} ✓`);
    } catch (e: any) {
      showToast('Push failed: ' + e.message, 'error');
    } finally {
      setPushingMeta(false);
    }
  }

  async function handleEnrichWithProspeo() {
    if (!pushToMeta) return;
    setEnriching(true);
    setEnrichResult('');
    try {
      const res = await apiPost<{ queued: boolean; count?: number; message?: string; personalEmailCount?: number }>(
        '/contacts/enrich',
        { clientId: pushToMeta.clientId, listName: pushToMeta.listName || undefined }
      );
      if (res.queued) {
        const total = res.count ?? 0;
        const mins = Math.ceil(total / 60);
        setEnrichResult(`Enriching ${total} contacts in the background — this takes ~${mins} min. You'll be notified here when done.`);

        // Poll every 30s to check if enrichment is complete
        if (enrichPollRef.current) clearInterval(enrichPollRef.current);
        enrichPollRef.current = setInterval(async () => {
          try {
            const check = await apiPost<{ queued: boolean; count?: number; message?: string; personalEmailCount?: number }>(
              '/contacts/enrich',
              { clientId: pushToMeta.clientId, listName: pushToMeta.listName || undefined }
            );
            if (!check.queued || (check.count ?? 0) === 0) {
              clearInterval(enrichPollRef.current!);
              enrichPollRef.current = null;
              const found = check.personalEmailCount ?? 0;
              if (found > 0) {
                setEnrichResult(`✓ Enrichment complete — found personal emails for ${found} contacts. Ready to push.`);
              } else {
                setEnrichResult(`✓ Enrichment complete — Prospeo couldn't find personal emails for this list (common for finance/corporate contacts). You can still push with work emails (~50% match rate).`);
              }
            } else {
              setEnrichResult(`Enriching ${check.count} contacts remaining… You'll be notified here when done.`);
            }
          } catch { /* ignore poll errors */ }
        }, 30_000);
      } else {
        setEnrichResult(res.message ?? 'No LinkedIn URLs found to enrich. Add a linkedinUrl column to your CSV.');
      }
    } catch (e: any) {
      setEnrichResult('Enrich failed: ' + e.message);
    } finally {
      setEnriching(false);
    }
  }

  async function handleCreateAudience() {
    if (!audName.trim()) return;
    const allSelected = contacts.filter(c => selected.has(c.id));
    if (allSelected.length === 0) return;

    // Determine client (all must be same client)
    const clientIds = [...new Set(allSelected.map(c => c.clientId))];
    if (clientIds.length > 1) { showToast('Selected contacts span multiple clients — filter to one client first', 'error'); return; }
    const clientId = clientIds[0]!;

    // Infer source/type from contact sources
    const allPixel = allSelected.every((c: any) => c.source === 'pixel');
    const anyPixel = allSelected.some((c: any) => c.source === 'pixel');
    const inferredSource: 'pixel' | 'manual' = allPixel ? 'pixel' : 'manual';
    const inferredType: 'direct' | 'seed'    = allPixel ? 'direct' : 'seed';

    setAudCreating(true);
    setAudProgress('Creating audience record…');
    try {
      const audRes = await db.models.Audience.create({
        clientId,
        audienceName: audName.trim(),
        audienceType: inferredType,
        source:       inferredSource,
        status:       'ready',
        memberCount:  allSelected.length,
      } as any);
      const aud = audRes.data;
      if (!aud) throw new Error('Failed to create audience');

      // Batch-create AudienceMember records
      const BATCH = 10;
      for (let i = 0; i < allSelected.length; i += BATCH) {
        setAudProgress(`Adding members ${Math.min(i + BATCH, allSelected.length)} of ${allSelected.length}…`);
        const batch = allSelected.slice(i, i + BATCH);
        await Promise.all(batch.map(c =>
          db.models.AudienceMember.create({
            audienceId: aud.id,
            contactId:  c.id,
            addedAt:    new Date().toISOString(),
          })
        ));
      }

      setAudModal(false);
      setAudName('');
      setSelected(new Set());
      showToast(`Audience "${audName.trim()}" created with ${allSelected.length} members`);

      // If non-pixel (seed), offer to push to ad platforms
      if (!allPixel) {
        const listName = [...new Set(allSelected.map((c: any) => c.listName).filter(Boolean))][0] ?? audName.trim();
        setPushPlatforms(new Set());
        setPushResults({});
        setPushToMeta({ clientId, listName, count: allSelected.length, personalEmailCount: allSelected.filter((c: any) => c.personalEmail).length });
      }
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setAudCreating(false);
      setAudProgress('');
    }
  }

  async function handleSuppress() {
    if (!suppressModal) return;
    setSuppressing(suppressModal.id);
    try {
      await apiPost('/contacts/suppress', {
        contactId:    suppressModal.id,
        clientId:     suppressModal.clientId,
        email:        suppressModal.email,
        reason:       suppressReason || 'admin_request',
        suppressedBy: 'admin-ui',
      });
      showToast(`${suppressModal.email} suppressed — removed from Meta on next sync`);
      setSuppressModal(null);
      setSuppressReason('');
      await load();
    } catch (e: any) {
      showToast('Suppress failed: ' + e.message, 'error');
    } finally {
      setSuppressing(null);
    }
  }

  const pixelContacts   = contacts.filter((c: any) => c.source === 'pixel').length;
  const prospeoContacts = contacts.filter((c: any) => c.source === 'prospeo').length;
  const suppressed      = contacts.filter(c => c.suppressed).length;
  const paginated       = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages      = Math.ceil(filtered.length / PAGE_SIZE);
  const clientMap       = Object.fromEntries(clients.map(c => [c.id, c]));

  const pageIds         = paginated.map(c => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Spinner size={28} />
    </div>
  );

  return (
    <div>
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleCsvFile}
      />
      <SectionHeader
        title="Contacts"
        sub={`${contacts.length.toLocaleString()} total contacts`}
        action={
          <Btn variant="ghost" size="sm" onClick={() => csvInputRef.current?.click()}>
            ↑ Import CSV
          </Btn>
        }
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        <Stat label="Total"          value={contacts.length}   accent />
        <Stat label="Pixel ✓"        value={pixelContacts}     sub="First-party" />
        <Stat label="Prospeo"        value={prospeoContacts}   sub="ICP enriched" />
        <Stat label="Suppressed"     value={suppressed}        sub="Excluded" />
      </div>

      {/* Filters */}
      <Card style={{ padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 180 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4, fontWeight: 500 }}>Search</div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Email, name, company, job title…"
              style={{
                width: '100%', background: '#0a1120', color: '#e2e8f0',
                border: '1px solid #1e2d42', borderRadius: 7,
                padding: '8px 12px', fontSize: 13,
                fontFamily: 'Sora, sans-serif', outline: 'none',
              }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <Select
              label="Client"
              value={clientFilter}
              onChange={e => setClientFilter(e.target.value)}
              options={[
                { value: '', label: 'All clients' },
                ...clients.map(c => ({ value: c.id, label: c.clientName ?? c.id })),
              ]}
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <Select
              label="Source"
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              options={[
                { value: '',        label: 'All sources'       },
                { value: 'pixel',   label: 'Pixel (consented)' },
                { value: 'prospeo', label: 'Prospeo (enriched)'},
                { value: 'manual',  label: 'Manual'            },
                { value: 'csv',     label: 'CSV import'        },
              ]}
            />
          </div>
          <div style={{ flex: 1, minWidth: 150 }}>
            <Select
              label="List"
              value={listFilter}
              onChange={e => setListFilter(e.target.value)}
              options={[
                { value: '', label: 'All lists' },
                ...Array.from(new Set(
                  contacts.flatMap((c: any) => c.listName ? [c.listName] : [])
                )).sort().map(n => ({ value: n, label: n })),
              ]}
            />
          </div>
          {(search || clientFilter || sourceFilter || listFilter) && (
            <Btn
              size="sm" variant="ghost"
              onClick={() => { setSearch(''); setClientFilter(''); setSourceFilter(''); setListFilter(''); }}
            >
              Clear
            </Btn>
          )}
        </div>
        {(search || clientFilter || sourceFilter || listFilter) && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#475569' }}>
            {filtered.length.toLocaleString()} of {contacts.length.toLocaleString()} contacts
          </div>
        )}
      </Card>

      {/* Table */}
      {filtered.length === 0 ? (
        <Empty
          icon="⊞"
          title="No contacts found"
          description="Adjust filters or run the pipeline to enrich contacts"
        />
      ) : (
        <>
          {/* Select-all bar (shown when nothing selected) */}
          {selected.size === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingLeft: 4 }}>
              <input
                type="checkbox"
                checked={false}
                onChange={toggleSelectAll}
                style={{ accentColor: '#3b82f6', width: 15, height: 15, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: '#475569' }}>Select page</span>
            </div>
          )}

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 16px', marginBottom: 8,
              background: '#0e1c30', border: '1px solid #1e3a5f', borderRadius: 8,
            }}>
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={toggleSelectAll}
                style={{ accentColor: '#3b82f6', width: 15, height: 15, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 13, color: '#93c5fd' }}>
                {selected.size} contact{selected.size !== 1 ? 's' : ''} selected
              </span>
              {selected.size < filtered.length && (
                <button
                  onClick={() => setSelected(new Set(filtered.map(c => c.id)))}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: '#60a5fa', textDecoration: 'underline', padding: 0,
                  }}
                >
                  Select all {filtered.length.toLocaleString()}
                </button>
              )}
              <Btn size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Btn>
              <Btn size="sm" onClick={() => { setAudName(''); setAudModal(true); }}>
                + Create Audience
              </Btn>
              <Btn size="sm" variant="danger" onClick={() => setDeleteModal(true)}>
                Delete selected
              </Btn>
            </div>
          )}

          <Card style={{ padding: 0 }}>
            <Table
              headers={['', 'Contact', 'Company / Role', 'Source', 'Consent', 'Client', 'Added', 'Actions']}
              rows={[
                ...paginated.map(contact => [
                // Checkbox
                <input
                  key="cb"
                  type="checkbox"
                  checked={selected.has(contact.id)}
                  onChange={() => toggleSelect(contact.id)}
                  style={{ accentColor: '#3b82f6', width: 15, height: 15, cursor: 'pointer' }}
                />,

                // Contact name + email
                <div key="name">
                  <div style={{ fontWeight: 500, color: '#e2e8f0', fontSize: 13 }}>
                    {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: '#3b82f6', fontFamily: 'DM Mono, monospace', marginTop: 2 }}>
                    {contact.email ?? '—'}
                  </div>
                  {contact.phone && (
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>{contact.phone}</div>
                  )}
                </div>,

                // Company
                <div key="company">
                  <div style={{ fontSize: 12, color: '#cbd5e1' }}>{contact.companyName ?? '—'}</div>
                  {contact.jobTitle && (
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>{contact.jobTitle}</div>
                  )}
                </div>,

                // Source
                <Badge key="source" label={(contact as any).source ?? 'unknown'} />,

                // Consent
                (contact as any).source === 'pixel'
                  ? contact.consentGiven
                    ? <span key="consent" style={{ fontSize: 11, color: '#10b981' }}>✓ granted</span>
                    : <span key="consent" style={{ fontSize: 11, color: '#475569' }}>pending</span>
                  : <span key="consent" style={{ fontSize: 11, color: '#253348' }}>N/A</span>,

                // Client
                <span key="client" style={{ fontSize: 12, color: '#64748b' }}>
                  {clientMap[contact.clientId ?? '']?.clientName ?? '—'}
                </span>,

                // Added date
                <span key="added" style={{ fontSize: 11, color: '#475569' }}>
                  {contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '—'}
                </span>,

                // Actions
                <div key="action" style={{ display: 'flex', gap: 6 }}>
                  {!contact.suppressed && (
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={() => { setSuppressModal(contact); setSuppressReason(''); }}
                    >
                      Suppress
                    </Btn>
                  )}
                  <Btn
                    size="sm"
                    variant="danger"
                    onClick={() => { setSelected(new Set([contact.id])); setDeleteModal(true); }}
                  >
                    Delete
                  </Btn>
                </div>,
              ])]}
            />
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
              <Btn size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                ← Prev
              </Btn>
              <span style={{ fontSize: 12, color: '#475569' }}>
                Page {page + 1} of {totalPages} · {filtered.length.toLocaleString()} contacts
              </span>
              <Btn size="sm" variant="ghost" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                Next →
              </Btn>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModal}
        onClose={() => { if (!deleting) { setDeleteModal(false); setSelected(new Set()); } }}
        title="Delete Contacts"
        width={440}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            padding: '12px 14px', borderRadius: 8,
            background: '#1c0a0a', border: '1px solid #991b1b',
            fontSize: 13, color: '#fca5a5', lineHeight: 1.6,
          }}>
            Permanently delete <strong>{selected.size} contact{selected.size !== 1 ? 's' : ''}</strong>?
            This cannot be undone. Deleted contacts will be removed from Meta on the next sync.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" onClick={() => { setDeleteModal(false); setSelected(new Set()); }} disabled={deleting}>
              Cancel
            </Btn>
            <Btn variant="danger" loading={deleting} onClick={handleDeleteSelected}>
              Delete {selected.size} contact{selected.size !== 1 ? 's' : ''}
            </Btn>
          </div>
        </div>
      </Modal>

      {/* Bulk delete progress overlay */}
      {deleting && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(2,8,20,0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#0d1627', border: '1px solid #1e3a5f', borderRadius: 12,
            padding: '28px 36px', textAlign: 'center', minWidth: 280,
          }}>
            <Spinner size={28} />
            <div style={{ marginTop: 16, fontSize: 14, color: '#93c5fd', fontWeight: 500 }}>
              {deleteProgress || 'Deleting…'}
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>
              Do not close or reload this page
            </div>
          </div>
        </div>
      )}

      {/* Suppress Modal */}
      <Modal
        open={!!suppressModal}
        onClose={() => setSuppressModal(null)}
        title="Suppress Contact"
        width={460}
      >
        {suppressModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '12px 14px', borderRadius: 8, background: '#080c14', border: '1px solid #1e2d42' }}>
              <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
                {[suppressModal.firstName, suppressModal.lastName].filter(Boolean).join(' ') || '—'}
              </div>
              <div style={{ fontSize: 12, color: '#3b82f6', fontFamily: 'DM Mono, monospace' }}>
                {suppressModal.email}
              </div>
            </div>

            <div style={{
              padding: '10px 14px', borderRadius: 7,
              background: '#1c0a0a', border: '1px solid #991b1b',
              fontSize: 12, color: '#fca5a5', lineHeight: 1.6,
            }}>
              This contact will be excluded from all future exports.
              The next Hightouch sync will remove them from the Meta Custom Audience automatically.
            </div>

            <Input
              label="Reason (optional)"
              value={suppressReason}
              onChange={e => setSuppressReason(e.target.value)}
              placeholder="e.g. unsubscribed, opt-out request, GDPR deletion"
            />

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #111927' }}>
              <Btn variant="secondary" onClick={() => setSuppressModal(null)}>Cancel</Btn>
              <Btn
                variant="danger"
                loading={suppressing === suppressModal.id}
                onClick={handleSuppress}
              >
                Confirm Suppress
              </Btn>
            </div>
          </div>
        )}
      </Modal>

      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Create Audience from Selection Modal ────────────────────────── */}
      <Modal
        open={audModal}
        onClose={() => { if (!audCreating) { setAudModal(false); setAudName(''); } }}
        title="Create Audience from Selection"
        width={500}
      >
        {(() => {
          const allSelected = contacts.filter(c => selected.has(c.id));
          const pixelCount  = allSelected.filter((c: any) => c.source === 'pixel').length;
          const csvCount    = allSelected.length - pixelCount;
          const allPixel    = csvCount === 0;
          const clientIds   = [...new Set(allSelected.map(c => c.clientId))];
          const multiClient = clientIds.length > 1;
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Source breakdown */}
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: '#06101e', border: '1px solid #1e2d42',
                fontSize: 12, lineHeight: 1.7,
              }}>
                <div style={{ marginBottom: 6, color: '#94a3b8', fontWeight: 500 }}>
                  {allSelected.length} contacts selected
                </div>
                {pixelCount > 0 && (
                  <div style={{ color: '#10b981' }}>
                    ✓ {pixelCount} pixel (first-party, consented)
                  </div>
                )}
                {csvCount > 0 && (
                  <div style={{ color: '#f59e0b' }}>
                    ◈ {csvCount} CSV / imported (third-party)
                  </div>
                )}
                <div style={{ marginTop: 8, color: allPixel ? '#10b981' : '#f59e0b', fontWeight: 600 }}>
                  {allPixel
                    ? 'Direct targeting — all contacts are first-party'
                    : 'Lookalike seed only — contains third-party contacts'}
                </div>
              </div>

              {multiClient && (
                <div style={{
                  padding: '10px 14px', borderRadius: 7,
                  background: '#1c0a0a', border: '1px solid #991b1b',
                  fontSize: 12, color: '#fca5a5',
                }}>
                  ⚠ Contacts span {clientIds.length} clients. Filter to one client before creating an audience.
                </div>
              )}

              <Input
                label="Audience name *"
                value={audName}
                onChange={e => setAudName(e.target.value)}
                placeholder={allPixel ? 'e.g. Pixel visitors Q2 2026' : 'e.g. Apollo ICP — SaaS Founders'}
              />

              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
                This will create an audience record and link all {allSelected.length} selected contacts to it.
                {!allPixel && ' After creation you can push to ad platforms as a lookalike seed.'}
              </div>

              {audProgress && (
                <div style={{ fontSize: 12, color: '#60a5fa' }}>{audProgress}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid #111927' }}>
                <Btn variant="ghost" onClick={() => { setAudModal(false); setAudName(''); }} disabled={audCreating}>
                  Cancel
                </Btn>
                <Btn
                  loading={audCreating}
                  disabled={!audName.trim() || multiClient}
                  onClick={handleCreateAudience}
                >
                  Create Audience
                </Btn>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ── CSV Import Modal ─────────────────────────────────────────────── */}
      <Modal
        open={csvModal}
        onClose={() => { if (!csvImporting) setCsvModal(false); }}
        title="Import Contacts from CSV"
        width={580}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Column map hint */}
          <div style={{
            padding: '10px 14px', borderRadius: 7,
            background: '#06101e', border: '1px solid #1e2d42',
            fontSize: 12, color: '#64748b', lineHeight: 1.7,
          }}>
            <strong style={{ color: '#94a3b8' }}>Recognised columns:</strong>
            {' '}email · firstName · lastName · phone · jobTitle · companyName ·
            companyDomain · linkedinUrl · seniority · department · city · state · country
          </div>

          <Select
            label="Assign to client *"
            value={csvClient}
            onChange={e => setCsvClient(e.target.value)}
            options={[
              { value: '', label: '— select client —' },
              ...clients.map(c => ({ value: c.id, label: c.clientName ?? c.id })),
            ]}
          />

          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4, fontWeight: 500 }}>
              List / segment name *
            </div>
            <input
              value={csvListName}
              onChange={e => setCsvListName(e.target.value)}
              placeholder='e.g. "Pharmacy owners NYC" or "High intent Q1"'
              style={{
                width: '100%', background: '#0a1120', color: '#e2e8f0',
                border: '1px solid #1e2d42', borderRadius: 7,
                padding: '8px 12px', fontSize: 13,
                fontFamily: 'Sora, sans-serif', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
              Becomes the Meta audience: <em style={{ color: '#60a5fa' }}>"{csvListName.trim() || 'List name'}"</em>
            </div>
          </div>

          {/* Preview */}
          <div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
              Preview — {csvRows.length.toLocaleString()} rows detected
            </div>
            <div style={{
              maxHeight: 160, overflowY: 'auto',
              background: '#060d18', border: '1px solid #1e2d42', borderRadius: 7,
              fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#94a3b8',
            }}>
              {csvRows.slice(0, 8).map((row, i) => (
                <div key={i} style={{
                  padding: '5px 10px',
                  borderBottom: i < 7 ? '1px solid #111927' : 'none',
                }}>
                  {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}{' · '}
                  <span style={{ color: '#3b82f6' }}>{row.email || '—'}</span>
                  {row.companyName ? ` · ${row.companyName}` : ''}
                </div>
              ))}
              {csvRows.length > 8 && (
                <div style={{ padding: '5px 10px', color: '#475569' }}>
                  …and {csvRows.length - 8} more
                </div>
              )}
            </div>
          </div>

          {csvErrors.length > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 7,
              background: '#120608', border: '1px solid #7f1d1d',
              fontSize: 12, color: '#fca5a5',
            }}>
              {csvErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {csvProgress && (
            <div style={{ fontSize: 12, color: '#60a5fa', textAlign: 'center' }}>
              {csvProgress}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <Btn variant="ghost" onClick={() => setCsvModal(false)} disabled={csvImporting}>
              Cancel
            </Btn>
            <Btn variant="primary" loading={csvImporting} onClick={importCsv}>
              Import {csvRows.length} contacts
            </Btn>
          </div>
        </div>
      </Modal>

      {/* ── Push to Ad Platform Modal ────────────────────────────────────── */}
      <Modal
        open={!!pushToMeta}
        onClose={() => { if (!pushingMeta && !enriching) { setPushToMeta(null); setEnrichResult(''); setPushResults({}); if (enrichPollRef.current) { clearInterval(enrichPollRef.current); enrichPollRef.current = null; } } }}
        title="Push Contacts to Ad Platforms"
        width={500}
      >
        {pushToMeta && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{
              padding: '12px 14px', borderRadius: 8,
              background: '#06101e', border: '1px solid #1e2d42',
              fontSize: 13, color: '#e2e8f0', lineHeight: 1.7,
            }}>
              <strong style={{ color: '#60a5fa' }}>
                {pushToMeta.count.toLocaleString()} contacts
              </strong>
              {' '}imported into list{' '}
              <strong style={{ color: '#e2e8f0' }}>"{pushToMeta.listName}"</strong>.
              <br />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Will upload as a <strong style={{ color: '#fbbf24' }}>lookalike seed</strong> — not for direct targeting.
              </span>
            </div>

            {/* Platform selection */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Select platforms
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { key: 'meta',     label: 'Meta'     },
                  { key: 'google',   label: 'Google'   },
                  { key: 'tiktok',   label: 'TikTok'   },
                  { key: 'twitter',  label: 'X Ads'    },
                  { key: 'linkedin', label: 'LinkedIn' },
                ].map(p => {
                  const on = pushPlatforms.has(p.key);
                  const result = pushResults[p.key];
                  return (
                    <span
                      key={p.key}
                      onClick={() => {
                        if (pushingMeta || result) return;
                        setPushPlatforms(prev => {
                          const next = new Set(prev);
                          on ? next.delete(p.key) : next.add(p.key);
                          return next;
                        });
                      }}
                      style={{
                        cursor: pushingMeta || result ? 'default' : 'pointer',
                        background: result ? (result.startsWith('✓') ? '#052e16' : '#1c0507') : on ? '#0c1a3d' : 'transparent',
                        border: `1px solid ${result ? (result.startsWith('✓') ? '#15803d' : '#7f1d1d') : on ? '#1d4ed8' : '#1e2d42'}`,
                        borderRadius: 6, padding: '5px 12px', fontSize: 12,
                        color: result ? (result.startsWith('✓') ? '#4ade80' : '#fca5a5') : on ? '#93c5fd' : '#475569',
                        userSelect: 'none', transition: 'all 0.1s',
                      }}
                    >
                      {result ? `${p.label} ${result}` : (on ? `✓ ${p.label}` : p.label)}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Prospeo enrichment option */}
            {pushToMeta && pushToMeta.personalEmailCount > 0 ? (
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: '#052e16', border: '1px solid #15803d',
                fontSize: 12, color: '#4ade80', lineHeight: 1.7, fontWeight: 500,
              }}>
                ✓ Personal emails already included — {pushToMeta.personalEmailCount.toLocaleString()} of {pushToMeta.count.toLocaleString()} contacts have personal emails from your CSV. No enrichment needed. Go ahead and push.
              </div>
            ) : (
              <div style={{
                padding: '12px 14px', borderRadius: 8,
                background: '#060d18', border: '1px solid #1e3a5f',
                fontSize: 12, color: '#94a3b8', lineHeight: 1.7,
              }}>
                <div style={{ fontWeight: 600, color: '#60a5fa', marginBottom: 6 }}>
                  ⚡ Enrich with Prospeo first (recommended)
                </div>
                If your CSV includes a <strong style={{ color: '#e2e8f0' }}>linkedinUrl</strong> column,
                Prospeo can find each person's <strong style={{ color: '#e2e8f0' }}>personal email</strong> (Gmail/Outlook)
                before pushing — boosting audience match rate from ~49% to <strong style={{ color: '#10b981' }}>70–85%</strong>.
                <br />
                <span style={{ color: '#475569' }}>Runs in background (~1 min per 80 contacts). Push to ad platform when done.</span>
                <div style={{ marginTop: 10 }}>
                  <Btn
                    variant="ghost"
                    size="sm"
                    loading={enriching}
                    onClick={handleEnrichWithProspeo}
                    style={{ borderColor: '#1e3a5f', color: '#60a5fa' }}
                  >
                    ⚡ Enrich emails with Prospeo
                  </Btn>
                </div>
                {enrichResult && (
                  <div style={{
                    marginTop: 10, padding: '8px 12px', borderRadius: 6,
                    background: enrichResult.startsWith('✓') ? '#052e16' : enrichResult.includes('failed') ? '#120608' : '#060f1e',
                    border: `1px solid ${enrichResult.startsWith('✓') ? '#15803d' : enrichResult.includes('failed') ? '#7f1d1d' : '#1e3a5f'}`,
                    color: enrichResult.startsWith('✓') ? '#4ade80' : enrichResult.includes('failed') ? '#fca5a5' : '#93c5fd',
                    fontSize: 12, lineHeight: 1.6, fontWeight: enrichResult.startsWith('✓') ? 600 : 400,
                  }}>
                    {enrichResult}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => { setPushToMeta(null); setEnrichResult(''); setPushResults({}); }} disabled={pushingMeta || enriching}>
                {Object.keys(pushResults).length > 0 ? 'Done' : 'Skip for now'}
              </Btn>
              {Object.keys(pushResults).length === 0 && (
                <Btn variant="primary" loading={pushingMeta} onClick={handlePushToMeta} disabled={enriching || pushPlatforms.size === 0}>
                  Push to {pushPlatforms.size} Platform{pushPlatforms.size !== 1 ? 's' : ''}
                </Btn>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
